/** Value at percentile `p` (0-100) of `values` (nearest rank). NaN when empty. */
export function percentile(values: readonly number[], p: number): number {
  if (values.length === 0) return Number.NaN;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))] as number;
}

/** The last `capacity` values, oldest first. */
export class Ring {
  readonly #values: number[] = [];
  readonly capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
  }

  push(value: number): void {
    this.#values.push(value);
    if (this.#values.length > this.capacity) this.#values.shift();
  }

  get values(): readonly number[] {
    return this.#values;
  }

  clear(): void {
    this.#values.length = 0;
  }
}

/**
 * Samples from the last `windowMs`, with the running minimum in O(1) (monotonic deque) and
 * percentiles of how far each sample sits above that minimum.
 */
export class TimeWindow {
  readonly windowMs: number;
  #samples: { at: number; value: number }[] = [];
  #mins: { at: number; value: number }[] = [];

  constructor(windowMs: number) {
    this.windowMs = windowMs;
  }

  push(at: number, value: number): void {
    this.#expire(at);
    this.#samples.push({ at, value });
    while (this.#mins.length > 0 && (this.#mins[this.#mins.length - 1]?.value ?? 0) >= value)
      this.#mins.pop();
    this.#mins.push({ at, value });
  }

  #expire(now: number): void {
    const cutoff = now - this.windowMs;
    let drop = 0;
    while (drop < this.#samples.length && (this.#samples[drop]?.at ?? 0) < cutoff) drop++;
    if (drop > 0) this.#samples = this.#samples.slice(drop);
    while (this.#mins.length > 0 && (this.#mins[0]?.at ?? 0) < cutoff) this.#mins.shift();
  }

  get size(): number {
    return this.#samples.length;
  }

  /** Raw values in the window, oldest first. */
  values(): number[] {
    return this.#samples.map((s) => s.value);
  }

  /** Smallest value in the window (NaN when empty). */
  get min(): number {
    return this.#mins[0]?.value ?? Number.NaN;
  }

  /** Percentile of (value − min) over the window: e.g. p95 of delivery delay above the fastest. */
  excess(p: number): number {
    const min = this.min;
    return percentile(
      this.#samples.map((s) => s.value - min),
      p,
    );
  }

  clear(): void {
    this.#samples = [];
    this.#mins = [];
  }
}
